import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BookOpen,
  Search,
  Download,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { formatCurrency } from "@/lib/billingUtils";

interface AccountingAccount {
  id: string;
  code: string;
  name: string;
  account_type: string;
  balance_debit: number;
  balance_credit: number;
  balance: number;
}

interface LedgerEntry {
  id: string;
  entry_id: string;
  entry_number: number;
  entry_date: string;
  description: string;
  reference?: string;
  debit: number;
  credit: number;
  line_description?: string;
  partner_name?: string;
}

export default function GeneralLedger() {
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(0, 1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Fetch accounts
  const { data: accounts = [] } = useQuery<AccountingAccount[]>({
    queryKey: ["accounting-accounts-ledger"],
    queryFn: async () => {
      const { data, error } = await api
        .from("accounting_accounts")
        .select("id, code, name, account_type, balance_debit, balance_credit, balance")
        .order("code");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch ledger entries for selected account
  const { data: entries = [], isLoading } = useQuery<LedgerEntry[]>({
    queryKey: ["ledger-entries", selectedAccountId, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedAccountId) return [];

      const { data, error } = await api
        .from("journal_entry_lines")
        .select(`
          id,
          entry_id,
          debit,
          credit,
          description,
          partner_name,
          journal_entries!inner(
            entry_number,
            entry_date,
            description,
            reference,
            status
          )
        `)
        .eq("account_id", selectedAccountId)
        .eq("journal_entries.status", "posted")
        .gte("journal_entries.entry_date", dateFrom)
        .lte("journal_entries.entry_date", dateTo)
        .order("journal_entries(entry_date)", { ascending: true });

      if (error) throw error;

      return (data || []).map((line: any) => ({
        id: line.id,
        entry_id: line.entry_id,
        entry_number: line.journal_entries?.entry_number || 0,
        entry_date: line.journal_entries?.entry_date || '',
        description: line.journal_entries?.description || '',
        reference: line.journal_entries?.reference,
        debit: line.debit || 0,
        credit: line.credit || 0,
        line_description: line.description,
        partner_name: line.partner_name,
      }));
    },
    enabled: !!selectedAccountId,
  });

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  // Calculate running balance
  const entriesWithBalance = useMemo(() => {
    let runningBalance = 0;
    return entries.map(entry => {
      runningBalance += entry.debit - entry.credit;
      return { ...entry, balance: runningBalance };
    });
  }, [entries]);

  // Totals
  const totals = useMemo(() => {
    const totalDebit = entries.reduce((sum, e) => sum + e.debit, 0);
    const totalCredit = entries.reduce((sum, e) => sum + e.credit, 0);
    return { totalDebit, totalCredit, balance: totalDebit - totalCredit };
  }, [entries]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Libro Mayor</h1>
            <p className="text-muted-foreground">
              Movimientos por cuenta contable
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label>Cuenta contable</Label>
                <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cuenta..." />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.code} - {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hasta</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        {selectedAccount && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                {selectedAccount.code} - {selectedAccount.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30">
                  <p className="text-sm text-muted-foreground">Total Debe</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.totalDebit)}</p>
                </div>
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30">
                  <p className="text-sm text-muted-foreground">Total Haber</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.totalCredit)}</p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                  <p className="text-sm text-muted-foreground">Saldo</p>
                  <p className={`text-2xl font-bold ${totals.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(totals.balance))}
                    {totals.balance >= 0 ? ' D' : ' H'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ledger Table */}
        <Card>
          <CardContent className="pt-6">
            {!selectedAccountId ? (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Selecciona una cuenta para ver sus movimientos</p>
              </div>
            ) : isLoading ? (
              <div className="text-center py-12">Cargando...</div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No hay movimientos en este periodo</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Asiento</TableHead>
                    <TableHead className="w-[100px]">Fecha</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead className="w-[120px]">Referencia</TableHead>
                    <TableHead className="w-[120px] text-right">Debe</TableHead>
                    <TableHead className="w-[120px] text-right">Haber</TableHead>
                    <TableHead className="w-[120px] text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entriesWithBalance.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-mono">{entry.entry_number}</TableCell>
                      <TableCell>{format(new Date(entry.entry_date), 'dd/MM/yyyy')}</TableCell>
                      <TableCell>
                        <div>{entry.description}</div>
                        {entry.line_description && entry.line_description !== entry.description && (
                          <div className="text-xs text-muted-foreground">{entry.line_description}</div>
                        )}
                        {entry.partner_name && (
                          <div className="text-xs text-blue-600">{entry.partner_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{entry.reference || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {entry.debit > 0 ? (
                          <span className="text-green-600 flex items-center justify-end gap-1">
                            <ArrowUpRight className="w-3 h-3" />
                            {formatCurrency(entry.debit)}
                          </span>
                        ) : ''}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {entry.credit > 0 ? (
                          <span className="text-red-600 flex items-center justify-end gap-1">
                            <ArrowDownRight className="w-3 h-3" />
                            {formatCurrency(entry.credit)}
                          </span>
                        ) : ''}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        <span className={entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(Math.abs(entry.balance))}
                          {entry.balance >= 0 ? ' D' : ' H'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow className="font-bold bg-muted/50">
                    <TableCell colSpan={4} className="text-right">TOTALES</TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {formatCurrency(totals.totalDebit)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      {formatCurrency(totals.totalCredit)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={totals.balance >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formatCurrency(Math.abs(totals.balance))}
                        {totals.balance >= 0 ? ' D' : ' H'}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
